import axios from 'axios';
import { useEffect, useState } from 'react';
import { ContentCard } from '../../components/ContentCard/ContentCard';
import { Button } from '../../components/Form/Button';
import { AddProductModal } from '../../components/editOrder/addProduct/AddProductModal';
import { SubscriptionListItem } from '../../components/subscriptions/SubscriptionListItem';
import { WITH_CREDENTIALS } from '../../components/utils';
import { useConfig } from '../../config';
import { useTranslation } from '../../i18n/i18n';
import { Res, useApi } from '../../state/api';
import { ApiCartItem, ApiDeliveryAddress, ApiList, ApiOrderItem } from '../../types';
import { Badge } from '../Badge/Badge';
import { FormValidationContainer } from '../Form/FormValidatorContainer';
import { Loader } from '../Loader/Loader';
import './Subscription.css';
import { TitleEditInput } from './TitleEditInput';
import { EditScheduleAddress } from './editSchedule/EditScheduleAddress';
import { EditScheduleModal } from './editSchedule/EditScheduleModal';

type SubscriptionProps = {
	list: ApiList;
	updateSubscriptions: () => void;
};

const SubscriptionContent = ({ list, updateSubscriptions }: SubscriptionProps): JSX.Element => {
	const { api } = useConfig();
	const translations = useTranslation();
	const t = { ...translations.subscriptions, cart: translations.cart, product: translations.product };
	const [loading, setLoading] = useState(false);
	const [selectedDeliveryAddress, setSelectedDeliveryAddress] = useState<string>('');

	const { data: schedule, mutate: mutateSchedule, isLoading } = useApi(Res.schedule, { listId: list.id });

	useEffect(() => {
		if (schedule?.deliveryAddress?.id !== undefined) {
			setSelectedDeliveryAddress(String(schedule.deliveryAddress.id));
		}
	}, [schedule?.deliveryAddress?.id]);

	const onAddProduct = async (item: ApiOrderItem): Promise<void> => {
		const productAlreadyAdded = list?.items.find((newItem) => newItem.product.id === item.product.id);
		const quantity = productAlreadyAdded ? (item.quantity += productAlreadyAdded.quantity) : item.quantity;

		await setQuantity(quantity, item.product.id);
	};

	const onRemoveProduct = async (item: ApiCartItem): Promise<void> => setQuantity(0, item.product.id);

	const onChangeProduct = async (item: ApiCartItem, quantity: number): Promise<void> =>
		setQuantity(quantity, item.product.id);

	const setQuantity = async (quantity: number, product: number) => {
		const payload = { list: list.id, product, quantity };

		await axios.post(`${api}me/lists/set-quantity.json`, payload, WITH_CREDENTIALS);

		updateSubscriptions();
	};

	const deleteList = async () => {
		setLoading(true);

		await axios.delete(`${api}me/lists/${list.id}.json`, WITH_CREDENTIALS);

		updateSubscriptions();
		setLoading(false);
	};

	const subscriptionStatus = (() => {
		if (!schedule) {
			return { label: t.noActiveSubscription, color: 'grey' as const };
		}

		if (schedule.deliveryAddress) {
			return { label: t.activeSchedule, color: 'green' as const };
		}

		return { label: t.noDeliveryAddressSelected, color: 'orange' as const };
	})();

	const onUpdate = (address?: ApiDeliveryAddress) => {
		if (schedule && schedule.id !== undefined) {
			mutateSchedule({ ...schedule, deliveryAddress: address }, true);
		}
	};

	if (isLoading) {
		return (
			<ContentCard className="subscription-products">
				<div className="subscription-header">
					<div style={{ flex: 1 }}>
						<Loader size="line" />
					</div>
				</div>
				<div style={{ marginTop: '2rem' }}>
					<Loader size="grid" />
				</div>
			</ContentCard>
		);
	}

	return (
		<ContentCard className="subscription-products">
			<div className="subscription-header">
				<TitleEditInput loading={loading} list={list} />

				<Badge size="small" color={subscriptionStatus.color}>
					{subscriptionStatus.label}
				</Badge>
			</div>
			<EditScheduleModal deleting={loading} list={list} />
			{schedule && (
				<EditScheduleAddress
					list={list}
					schedule={schedule}
					onUpdate={onUpdate}
					setSelectedDeliveryAddress={setSelectedDeliveryAddress}
					selectedDeliveryAddress={selectedDeliveryAddress}
				/>
			)}
			{Boolean(list.items.length) && (
				<subscription-items>
					<div className="not-mobile">
						<subscription-item-heading>
							<span>{t.cart.product}</span>
							<span>{t.cart.amount}</span>
							<span>{t.product.artnr}</span>
							<span className="text-right">{t.cart.total}</span>
						</subscription-item-heading>
					</div>
					{list.items.map((item) => (
						<SubscriptionListItem
							onDelete={onRemoveProduct}
							onChange={onChangeProduct}
							key={item.product.id}
							item={item}
						/>
					))}
				</subscription-items>
			)}
			<div className="subscription-footer">
				<AddProductModal deleting={loading} onSave={onAddProduct} title={t.addProductTitle} />
				<Button
					className="secondary"
					disabled={loading}
					style={{ marginBottom: '1.6rem', fontSize: '16px' }}
					onClick={deleteList}
				>
					{!loading ? t.deleteList : t.deletingList}
				</Button>
			</div>
		</ContentCard>
	);
};

export const Subscription = (props: SubscriptionProps): JSX.Element => (
	<FormValidationContainer.Provider>
		<SubscriptionContent {...props} />
	</FormValidationContainer.Provider>
);
